// hooks
import {
  useCommonInfiniteQueryWithScroll,
  useCommonMutation,
  useCommonQuery,
  type UseCommonInfiniteQueryWithScrollOptionsType
} from 'hooks';

// mopdules
import type {
  BaseApiResponse,
  BaseSearchRequest,
  ReqCompanyDetail,
  ResCompanyNames,
  ResultError,
  useGetCompanyNamesInfiniteResult,
  ReqCreateCompany,
  ReqUpdateCompany,
  ReqUpdateCompanyFiles,
  ReqCompanyList,
  CompanyInfo
} from 'modules';

// apis
import { CompanyAPI } from '../apis/company.api';

// keys
import { companyKeys } from '../keys/company.key';

export const companyApi = new CompanyAPI();

/** 업체 이름 조회 쿼리 훅 - 무한스크롤 */
export const useGetCompanyNamesInfinite = ({
  data,
  options
}: {
  data: BaseSearchRequest;
  options?: UseCommonInfiniteQueryWithScrollOptionsType<
    BaseApiResponse<unknown, ResCompanyNames>,
    ResultError
  >;
}): useGetCompanyNamesInfiniteResult => {
  const queryKeyFactory = (data: BaseSearchRequest) => companyKeys.GET_COMPANY_NAMES(data);

  const queryFn = async (
    data: BaseSearchRequest,
    { page, size }: { page: number; size: number }
  ) => {
    return await companyApi.getCompanyNames({ ...data, page, size });
  };

  return useCommonInfiniteQueryWithScroll({
    data,
    options,
    queryKeyFactory,
    queryFn
  });
};

export const useCreateCompany = () => {
  return useCommonMutation({
    mutationKey: ['CREATE_COMPANY'],
    mutationFn: (request: ReqCreateCompany) => {
      const formData = new FormData();
      formData.append('company', JSON.stringify(request.createCompany));

      if (request.file) {
        formData.append('file', request.file);
      }

      return companyApi.createCompany(formData);
    },
    queryKeyToInvalidate: ['COMPANY_LIST']
  });
};

export const useFetchCompanyDetail = ({ data }: { data: ReqCompanyDetail }) => {
  return useCommonQuery({
    data,
    queryFn: () => companyApi.getCompanyDetail(data),
    queryKeyFactory: (data: ReqCompanyDetail) => companyKeys.GET_COMPANY_DETAIL(data),
    options: {
      enabled: !!data.companyId,
      select: (data) => data.resultObject
    }
  });
};

export const useUpdateCompany = () => {
  return useCommonMutation({
    mutationKey: companyKeys.UPDATE_COMPANY(),
    mutationFn: (request: ReqUpdateCompany) => companyApi.updateCompany(request),
    queryKeyToInvalidate: companyKeys.GET_COMPANY_DETAIL()
  });
};

export const useUpdateCompanyFiles = () => {
  return useCommonMutation({
    mutationKey: ['UPDATE_COMPANY_FILE'],
    mutationFn: (request: ReqUpdateCompanyFiles) => {
      const formData = new FormData();

      request.files.forEach((file) => {
        formData.append('file', file);
      });

      return companyApi.updateCompanyFiles(request.comapnyId, formData);
    },
    queryKeyToInvalidate: ['COMPANY_DETAIL']
  });
};

export const useDeleteCompanyFiles = () => {
  return useCommonMutation({
    mutationKey: ['DELETE_COMPANY_FILE'],
    mutationFn: (companyId: string) => companyApi.deleteCompanyFiles(companyId),
    queryKeyToInvalidate: ['COMPANY_DETAIL']
  });
};

export const useFetchCompanies = ({ data }: { data: ReqCompanyList }) => {
  return useCommonQuery<
    ReqCompanyList,
    BaseApiResponse<unknown, CompanyInfo>,
    ResultError,
    CompanyInfo[]
  >({
    data,
    options: {
      select: (data) => data.resultList
    },
    queryKeyFactory: (data) => ['COMPANY_LIST', data],
    queryFn: (data, pagination) => {
      const page = {
        page: pagination.current,
        size: pagination.pageSize
      };

      return companyApi.getCompanies({
        ...data,
        ...page
      });
    }
  });
};
